import React, { useCallback, useMemo } from 'react';
import _, { PropertyPath } from 'lodash';

import Conditional from '../../../common/conditional';
import CustomSection from '../../custom-general/custom-section';
import PDFTablePage from '../../../themed/pdf/pdfTablePage';

import { Column, NewAnomalyItem } from '../../../../utils/models';
import { ANOMALY_TYPE, DAMAGE_CLASS } from '../../../../utils/constants';
import { concatClassNames, formatAnomalyText, getAnomalyClassStyle, getAnomalyTypeClassFromAnomaly, getAnomalyTypeStyle, getChunkedData, getIsAnomalyType, getMaxAnomalyType, getMaxClassFromInspectedItem, matchPhrases } from '../../../../utils/utilities';

import useCoverSheetOverview from '../../../../hooks/useCoverSheetOverview';
import useProjectDetails from '../../../../hooks/useProjectDetails';

import classNames from './style.module.scss';

interface DamageClassAnomaly {
	id: number;
	anomalyItems: NewAnomalyItem[];
	hasAnomalies: 'yes' | 'no';
	hasPID?: 'yes' | 'no';
	pIDNumber?: string;
	inspectionDescription: string;
}

interface DamageClassSummaryProps {
	itemNumberKey: string;
	columns: Column[];
	items: DamageClassAnomaly[];
}

const DamageClassSummary: React.FC<DamageClassSummaryProps> = ({ items, itemNumberKey, columns }) => {
	const overview = useCoverSheetOverview();
	const projectDetails = useProjectDetails();

	const isAnomalyTpe = useMemo(() => {
		return projectDetails.projectDetails && getIsAnomalyType(projectDetails.projectDetails);
	}, [projectDetails.projectDetails]);

	const reportTitle = useMemo(() => {
		return isAnomalyTpe ? 'Anomaly Class Summary' : 'Damage Class Summary';
	}, [isAnomalyTpe]);

	const damageClassColumns: Column[] = useMemo(() => {
		const defaultColumns = [
			{
				path: 'id',
				label: 'Sr. No',
			},
		];

		return defaultColumns.concat(columns); 
	}, [columns]);

	const generalColumns: Column[] = useMemo(() => {
		return [
			{
				path: 'id',
				label: 'Sr. No',
			},
			...columns,
			{
				path: 'pIDNumber',
				label: 'P&ID Number',
				content: (item: DamageClassAnomaly, itemIndex) => {
					if (item.hasPID) {
						if (matchPhrases(item.hasPID, 'yes')) {
							return <span>{item.pIDNumber || 'N/A'}</span>;
						}

						if (matchPhrases(item.hasPID, 'no')) {
							return <span>No P&ID Available</span>;
						}

						const itemIndex = items.findIndex((i) => i.id === item.id);
						const itemWithPID = items
							.slice(0, itemIndex)
							.find((inspectionItem) => inspectionItem.pIDNumber);

						return (
							<span>
								{itemWithPID ? itemWithPID.pIDNumber : 'No P&ID Available'}
							</span>
						);
					}

					return <span>No P&ID Available</span>;
				},
			},
			{
				path: 'anomalyItems',
				label: isAnomalyTpe ? 'Anomaly Class' : 'Damage Class',
				content: (item: DamageClassAnomaly) => {
					if (matchPhrases(item.hasAnomalies, 'yes') && item.anomalyItems.length > 0) {
						const sorted = _.uniqBy(_.orderBy(item.anomalyItems, [(anomaly) => isAnomalyTpe ? anomaly.class : +anomaly.class], ['desc']), (anomaly) => isAnomalyTpe ? anomaly.class : +anomaly.class);
						const mappedAnomalies = sorted.map((anomaly) => {
							const name  = `${anomaly.class}${isAnomalyTpe ? ' ' + getAnomalyTypeClassFromAnomaly(anomaly.class.toString()) : ''}`;

							return { id: name, name };
						});

						return <span>{formatAnomalyText(mappedAnomalies)}</span>;
					}

					return <span>{item.inspectionDescription}</span>;
				},
				style: (item) => isAnomalyTpe ? getAnomalyTypeStyle({ class: getMaxAnomalyType(item) }) : getAnomalyClassStyle({ class: getMaxClassFromInspectedItem(item) })
			},
			{
				path: 'anomalyItems',
				label: 'Number of Anomalies',
				content: (item: DamageClassAnomaly) => {
					if (matchPhrases(item.hasAnomalies, 'yes')) {
						return item.anomalyItems.length;
					}

					return <span>N/A</span>;
				},
			},
			{
				path: 'pageNumber',
				label: 'Page Number',
				content: (item: DamageClassAnomaly) => {
					const identificationNumber = item[itemNumberKey as keyof typeof item] as PropertyPath;
					const match = overview.find((item) => item.subpages ? _.get(item.subpages, identificationNumber) : null);

					if (match) {
						const subpages = _.get(match.subpages, identificationNumber);
						const subpageRange =
							subpages.length > 1
								? `${subpages[0].page} - ${subpages.at(-1).page}`
								: subpages[0].page;

						return <span>{subpageRange}</span>;
					}

					return <span></span>;
				},
			},
		];
	}, [columns, isAnomalyTpe, items, itemNumberKey, overview]);

	const updatedInspectionItems = useMemo(() => {
		return items.map((item) => ({
			...item,
			maxClass: isAnomalyTpe ? getMaxAnomalyType(item) : getMaxClassFromInspectedItem(item),
		}))
	}, [isAnomalyTpe, items]);

	const getClassAnomalyLines = useCallback((damageClass: string | number) => {
		const filteredItems = updatedInspectionItems
			.filter((item) => matchPhrases(item.hasAnomalies, 'yes') && item.maxClass === damageClass)
			.map((item, index) => ({ ...item, id: index + 1 }));

		return filteredItems;
	}, [updatedInspectionItems]);

	const getClassAnomalyLinesNoAnomaly = useCallback(() => {
		const filteredItems = items
			.filter((item) => matchPhrases(item.hasAnomalies, 'no') && item.anomalyItems.length === 0)
			.map((item, index) => ({ ...item, id: index + 1 }));

		return filteredItems;
	}, [items]);

	const getChunkedDamageClasses = useCallback((damageClasses: (number | string)[]) => {
		const mapped = damageClasses.map((damageClass) => ({
			class: damageClass,
			data: getChunkedData(getClassAnomalyLines(damageClass), 18),
		}));

		const maxChunk = Math.max(...mapped.map((item) => item.data.length));
		if (maxChunk >= 1) {
			const transformedItems = mapped.map((mappedItem) => ({
				...mappedItem,
				data: mappedItem.data.length === maxChunk
						? mappedItem.data
						: [...mappedItem.data].concat(Array(maxChunk - mappedItem.data.length).fill([])),
			}));
			return transformedItems;
		}

		return mapped;
	}, [getClassAnomalyLines]);

	const upperDamageClasses = useMemo(() => {
		const classes = isAnomalyTpe ? [ANOMALY_TYPE.MAJOR.ID] : [DAMAGE_CLASS.SIX.ID, DAMAGE_CLASS.FIVE.ID, DAMAGE_CLASS.FOUR.ID];
		return getChunkedDamageClasses(classes);
	}, [getChunkedDamageClasses, isAnomalyTpe]);

	const lowerDamageClasses = useMemo(() => {
		const classes = isAnomalyTpe ? [ANOMALY_TYPE.MODERATE.ID, ANOMALY_TYPE.MINOR_TO_MODERATE.ID, ANOMALY_TYPE.MINOR.ID] : [DAMAGE_CLASS.THREE.ID, DAMAGE_CLASS.TWO.ID, DAMAGE_CLASS.ONE.ID];
		return getChunkedDamageClasses(classes);
	}, [getChunkedDamageClasses, isAnomalyTpe]);

	return (
		<>
			{getChunkedData(items, 20).map((dataItems, index) => (
				<CustomSection key={index} sectionTitle={reportTitle}>
					<div className={classNames.damageClass}>
						<PDFTablePage data={dataItems} columns={generalColumns} />
					</div>
				</CustomSection>
			))}

			{Array(upperDamageClasses[0].data.length)
				.fill('')
				.map((_, index) => (
					<CustomSection key={index} sectionTitle={reportTitle}>
						<div
							className={concatClassNames([
								classNames.damageClass,
								classNames.damageClassTableHeight,
							])}
						>
							<div className="container-fluid">
								<div className="row">
									{upperDamageClasses.map((damageClass) => (
										<Conditional key={damageClass.class} isVisible={damageClass.data[index].length > 0}>
											<div className="col p-1">
												<div className={classNames.damageClassHeader} style={isAnomalyTpe ?  getAnomalyTypeStyle({ class: damageClass.class }) : getAnomalyClassStyle({ class: +damageClass.class })}>
													{reportTitle.toLowerCase().split('summary')[0]}: {damageClass.class}
												</div>

												<div className="mt-1">
													<PDFTablePage
														data={damageClass.data[index]}
														columns={damageClassColumns}
													/>
												</div>
											</div>
										</Conditional>
									))}
								</div>
							</div>
						</div>
					</CustomSection>
				))}

			{Array(lowerDamageClasses[0].data.length)
				.fill('')
				.map((_, index) => (
					<CustomSection key={index} sectionTitle={reportTitle}>
						<div className={concatClassNames([ classNames.damageClass, classNames.damageClassTableHeight])}>
							<div className="container-fluid">
								<div className="row">
									{lowerDamageClasses.map((damageClass) => (
										<div className="col p-1" key={damageClass.class}>
											<div className={classNames.damageClassHeader} style={isAnomalyTpe ?  getAnomalyTypeStyle({ class: damageClass.class }) : getAnomalyClassStyle({ class: +damageClass.class })}>
												{reportTitle.toLowerCase().split('summary')[0]}: {damageClass.class}
											</div>

											<div className="mt-1">
												<PDFTablePage
													data={damageClass.data[index]}
													columns={damageClassColumns}
												/>
											</div>
										</div>
									))}
								</div>
							</div>
						</div>
					</CustomSection>
				))}

			{getChunkedData(getClassAnomalyLinesNoAnomaly(), 30).map((dataItems, index) => (
				<CustomSection key={index} sectionTitle={reportTitle}>
					<div className={classNames.damageClass}>
						<div className={concatClassNames([classNames.damageClassHeader,classNames.damageClassHeaderGreen])}>
							No Anomaly
						</div>

						<PDFTablePage data={dataItems} columns={damageClassColumns} />
					</div>
				</CustomSection>
			))}
		</>
	);
};

export default DamageClassSummary;
